import React from "react";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Typography,
} from "@mui/material";
import { CategoryOutlined } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import type { Category } from "../../types";
import { EntityStatus } from "../../types";

function categoryAvatarColor(name: string): string {
  const colors = [
    "#4285f4",
    "#34a853",
    "#ea4335",
    "#fbbc04",
    "#673ab7",
    "#00acc1",
  ];
  return colors[name.length % colors.length];
}

interface CategoryExplorerViewsProps {
  category: Category;
  productCount?: number;
}

export const CategoryExplorerCard: React.FC<CategoryExplorerViewsProps> = ({
  category,
  productCount = 0,
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 2,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 3 },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/category-explorer/${category.uuid}`}
        sx={{ height: "100%", alignItems: "stretch" }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: categoryAvatarColor(category.name),
                width: 40,
                height: 40,
              }}
            >
              <CategoryOutlined fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} noWrap>
                {category.name}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            <Chip
              label={category.status}
              size="small"
              color={
                category.status === EntityStatus.ACTIVE ? "success" : "default"
              }
            />
            <Chip
              label={`${productCount} product${productCount === 1 ? "" : "s"}`}
              size="small"
              variant="outlined"
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {category.description?.trim() || "No description available"}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export const CategoryExplorerListRow: React.FC<CategoryExplorerViewsProps> = ({
  category,
  productCount = 0,
}) => {
  return (
    <Box
      component={RouterLink}
      to={`/category-explorer/${category.uuid}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        textDecoration: "none",
        color: "inherit",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Avatar
        sx={{
          bgcolor: categoryAvatarColor(category.name),
          width: 40,
          height: 40,
        }}
      >
        <CategoryOutlined fontSize="small" />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {category.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {category.description?.trim() || "No description"}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 88 }}>
        {productCount} product{productCount === 1 ? "" : "s"}
      </Typography>
      <Chip
        label={category.status}
        size="small"
        color={category.status === EntityStatus.ACTIVE ? "success" : "default"}
      />
    </Box>
  );
};
